import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiBuilder,
  AppConfigService,
  ApplicationConfig,
  AppTokenService,
  handleError,
  HttpHeadersPayload,
  HttpRequestMethods,
  IDP_APPS,
  OauthAEMConfig,
  ReplaceItem,
  UserDataService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { EndpointKeysCommon } from '@com-delta-idp/mnx-sitewide-shared-utils';
import { forkJoin, Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { PROFILE_SELECTOR_IDP_APPS } from '../../constants/profile-selector-constants';
import { ProfileSelectorEndpointsKeys } from '../../constants/profile-selector.enums';
import { ProfileSelectorAEM } from '../../models/profile-selector-aem.interface';

@Injectable({
  providedIn: 'root'
})
export class ProfileSelectorService {
  http = inject(HttpClient);
  appConfig = inject(AppConfigService);
  applicationConfig = inject(ApplicationConfig);
  userData = inject(UserDataService);
  appToken = inject(AppTokenService);

  private headerInfo: HttpHeadersPayload = {
    'Content-Type': 'application/json'
  };

  _errorHandler = handleError.bind(this);

  getAEMContentServiceCall = (): Observable<ProfileSelectorAEM> => {
    return new ApiBuilder<undefined, ProfileSelectorAEM>(
      this.http,
      this.appConfig
    )
      .app(PROFILE_SELECTOR_IDP_APPS)
      .endpointKey(ProfileSelectorEndpointsKeys['PROFILE_SELECTOR_AEM_URL'])
      .method(HttpRequestMethods['GET'])
      .errorHandler(this._errorHandler)
      .library('PROFILE_SELECTOR_AEM')
      .create();
  };

  getStaticContentForProfileSelectorScreen(): Observable<
    [ProfileSelectorAEM, OauthAEMConfig]
  > {
    return forkJoin([
      this.getAEMContentServiceCall(),
      this.userData.getAuthConfigContent()
    ]);
  }

  postAccountTypeCall = (acctType: string): Observable<string> => {
    return new ApiBuilder<{ cacheKey: string; value: string }, string>(
      this.http,
      this.appConfig
    )
      .app(PROFILE_SELECTOR_IDP_APPS)
      .endpointKey(ProfileSelectorEndpointsKeys['SESS_CUSTOMER_EDP_DATA_URL'])
      .method(HttpRequestMethods['POST'])
      .payload({
        cacheKey: 'edpCorpData',
        value: acctType
      })
      .httpHeaders(this.headerInfo)
      .errorHandler(this._errorHandler)
      .library('SESS_CUSTOMER_EDP_DATA')
      .create();
  };

  postStoreCorporateCompanyId = (companyId: string): Observable<object> => {
    return this.userData.getAuthConfigContent().pipe(
      concatMap((config) => {
        return this.appToken.getAppToken(config);
      }),
      concatMap((token) => {
        const authorizationHeader = {
          ...this.headerInfo,
          Authorization: `Bearer ${token.access_token}`,
          transactionId: ''
        };

        return new ApiBuilder<{ companyId: string }, object>(
          this.http,
          this.appConfig
        )
          .app(PROFILE_SELECTOR_IDP_APPS)
          .endpointKey(
            ProfileSelectorEndpointsKeys['STORE_CORPORATE_COMPANY_ID']
          )
          .method(HttpRequestMethods['POST'])
          .payload({ companyId })
          .httpHeaders(authorizationHeader)
          .errorHandler(this._errorHandler)
          .library('STORE_CORPORATE_COMPANY_ID')
          .create();
      })
    );
  };

  getAccountType = (
    cacheKey: string
  ): Observable<{
    cacheKey: string;
    value: string;
  }> => {
    //TODO: get url from idp-config.json
    const endpointUrl = '/pref/prefsController/retrieveDetails';

    const finalUrl: ReplaceItem[] = [
      {
        src: '/content/dam/delta-applications/login/auth-config.json',
        target: `${endpointUrl}?CacheKey=${cacheKey}`
      }
    ];

    return new ApiBuilder<
      undefined,
      {
        cacheKey: string;
        value: string;
      }
    >(this.http, this.appConfig)
      .app(IDP_APPS.COMMON)
      .endpointKey(EndpointKeysCommon.APP_AUTH_CONFIG_JSON)
      .overrideEndpoint(finalUrl)
      .method(HttpRequestMethods['GET'])
      .httpHeaders(this.headerInfo)
      .errorHandler(this._errorHandler)
      .library('SITE_WIDE_SHARED_CORE')
      .create();
  };
}
